function createSerialId(api) {
  return `${api}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeBridgeError(error) {
  if (!error) {
    return { message: '未知錯誤' };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  return {
    resultCode: error.resultCode || error.result_code || error.resultStatus || error.result_status,
    error: error.error,
    errorMessage: error.errorMessage,
    errorDesc: error.errorDesc,
    message: error.message,
  };
}

function describePaymentUrl(paymentUrl) {
  if (!paymentUrl || typeof paymentUrl !== 'string') {
    return {};
  }

  const match = paymentUrl.match(/^([a-z][a-z0-9+.-]*:\/\/[^/?#]+)/i);

  try {
    const url = new URL(paymentUrl);
    const origin = url.origin && url.origin !== 'null'
      ? url.origin
      : (match ? match[1] : undefined);

    return {
      paymentUrl,
      paymentUrlOrigin: origin,
      paymentUrlProtocol: url.protocol,
      paymentUrlLength: paymentUrl.length,
    };
  } catch (e) {
    return {
      paymentUrl,
      paymentUrlOrigin: match ? match[1] : undefined,
      paymentUrlLength: paymentUrl.length,
    };
  }
}

export function getAlipayHKPaymentUrlOrigin(paymentUrl) {
  return describePaymentUrl(paymentUrl).paymentUrlOrigin || null;
}

export function isUnsupportedAlipayHKTradePayPaymentUrl(paymentUrl) {
  return false;
}

function listObjectKeys(value) {
  return value && typeof value === 'object' ? Object.keys(value) : [];
}

function unwrapTradePayResult(result) {
  if (!result || typeof result !== 'object') {
    return result;
  }

  const nestedResult = result.result || result.data || result.response;

  if (nestedResult && typeof nestedResult === 'object') {
    return nestedResult;
  }

  return result;
}

function normalizeTradePayResult(result) {
  const normalized = unwrapTradePayResult(result);

  if (!normalized || typeof normalized !== 'object') {
    return { message: normalized };
  }

  return {
    ...normalized,
    resultCode:
      normalized.resultCode ||
      normalized.result_code ||
      normalized.resultStatus ||
      normalized.result_status,
  };
}

function describeTradePayParams(params = {}) {
  return {
    hasPaymentUrl: Boolean(params.paymentUrl),
    ...describePaymentUrl(params.paymentUrl),
    hasOrderStr: Boolean(params.orderStr),
    orderStrLength: params.orderStr ? params.orderStr.length : 0,
    hasTradeNO: Boolean(params.tradeNO),
  };
}

function describeTradePayResult(result) {
  const normalized = normalizeTradePayResult(result);

  if (!normalized || typeof normalized !== 'object') {
    return { message: result };
  }

  return {
    rawType: typeof result,
    rawKeys: listObjectKeys(result),
    normalizedKeys: listObjectKeys(normalized),
    resultCode: normalized.resultCode,
    resultStatus: normalized.resultStatus || normalized.result_status,
    error: normalized.error,
    errorMessage: normalized.errorMessage,
    errorDesc: normalized.errorDesc,
    message: normalized.message,
  };
}

function sanitizeTraceValue(value) {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
    };
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeTraceValue(item));
  }

  if (value && typeof value === 'object') {
    return Object.keys(value).reduce((safeValue, key) => {
      safeValue[key] = sanitizeTraceValue(value[key]);
      return safeValue;
    }, {});
  }

  return value;
}

function postAlipayHKTraceToMiniapp(level, stage, context, source = 'AlipayHKTradePay') {
  if (
    typeof my === 'undefined' ||
    typeof my.postMessage !== 'function'
  ) {
    return;
  }

  try {
    my.postMessage({
      api: 'debugLog',
      options: {
        source,
        level,
        stage,
        context: sanitizeTraceValue(context),
      },
      serialId: createSerialId('debugLog'),
    });
  } catch (e) {
    // Debug forwarding must never affect payment flow.
  }
}

export function postAlipayHKDebugLog(source, level, stage, context = {}) {
  postAlipayHKTraceToMiniapp(level, stage, context, source);
}

function logAlipayHKTradePay(level, stage, context = {}) {
  if (typeof console !== 'undefined' && typeof console[level] === 'function') {
    console[level](`[AlipayHKTradePay] ${stage}`, context);
  }

  postAlipayHKTraceToMiniapp(level, stage, context);
}

function hasDirectAlipayHKTradePay() {
  return (
    typeof my !== 'undefined' &&
    typeof my.tradePay === 'function'
  );
}

function hasAlipayHKMiniProgramBridge() {
  return (
    typeof my !== 'undefined' &&
    typeof my.postMessage === 'function'
  );
}

function waitForAlipayHKTradePay(timeout = 3000, interval = 50) {
  if (hasDirectAlipayHKTradePay()) {
    return Promise.resolve(true);
  }

  if (typeof window === 'undefined') {
    return Promise.resolve(false);
  }

  return new Promise((resolve) => {
    const startedAt = Date.now();

    const timer = setInterval(() => {
      if (hasDirectAlipayHKTradePay()) {
        clearInterval(timer);
        resolve(true);
        return;
      }

      if (Date.now() - startedAt >= timeout) {
        clearInterval(timer);
        resolve(false);
      }
    }, interval);
  });
}

function callDirectAlipayHKTradePay(params, timeout = 120000) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let timeoutId = null;

    const settle = (callback, value) => {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(timeoutId);
      callback(value);
    };

    try {
      logAlipayHKTradePay('info', 'direct_start', describeTradePayParams(params));
      timeoutId = setTimeout(() => {
        logAlipayHKTradePay('error', 'direct_timeout', describeTradePayParams(params));
        settle(reject, new Error('trade_pay_timeout'));
      }, timeout);

      my.tradePay({
        ...params,
        success: (result) => {
          logAlipayHKTradePay('info', 'direct_success', describeTradePayResult(result));
          settle(resolve, {
            ...normalizeTradePayResult(result),
            paymentUrl: params.paymentUrl,
          });
        },
        fail: (error) => {
          const normalized = normalizeBridgeError(error);
          logAlipayHKTradePay('error', 'direct_fail', {
            ...describeTradePayParams(params),
            ...normalized,
          });
          settle(reject, normalized);
        },
        complete: (result) => {
          logAlipayHKTradePay('info', 'direct_complete', describeTradePayResult(result));
          const normalized = normalizeTradePayResult(result);
          if (normalized?.resultCode || normalized?.error || normalized?.errorMessage) {
            settle(resolve, {
              ...normalized,
              paymentUrl: params.paymentUrl,
            });
          }
        },
      });
    } catch (e) {
      const normalized = normalizeBridgeError(e);
      logAlipayHKTradePay('error', 'direct_exception', {
        ...describeTradePayParams(params),
        ...normalized,
      });
      settle(reject, normalized);
    }
  });
}

async function callBridgeAlipayHKTradePay(params, timeout = 120000) {
  logAlipayHKTradePay('info', 'bridge_start', describeTradePayParams(params));

  try {
    const result = await callAlipayHKMiniProgramApi('tradePay', params, timeout);

    logAlipayHKTradePay('info', 'bridge_result', describeTradePayResult(result));

    return {
      ...normalizeTradePayResult(result),
      paymentUrl: params.paymentUrl,
    };
  } catch (e) {
    const normalized = normalizeBridgeError(e);

    logAlipayHKTradePay('error', 'bridge_error', {
      ...describeTradePayParams(params),
      ...normalized,
    });

    throw normalized;
  }
}

export function normalizeAlipayHKTradePayResult(result) {
  return normalizeTradePayResult(result);
}

export function resolveAlipayHKTradePayParams(payload = {}) {
  const data = payload || {};
  const source = data.trade_pay_params || data.tradePayParams || data;
  const params = {};

  const paymentUrl =
    source.paymentUrl ||
    source.payment_url ||
    source.pay_url ||
    data.paymentUrl ||
    data.payment_url ||
    data.pay_url;

  if (paymentUrl) {
    params.paymentUrl = paymentUrl;
  }

  return params.paymentUrl ? params : null;
}

export function isDirectAlipayHKTradePayPayload(payload = {}) {
  const params = resolveAlipayHKTradePayParams(payload);

  if (!params) {
    return false;
  }

  return Boolean(params.paymentUrl);
}

export function getAlipayHKFallbackPayUrl(payload = {}) {
  if (typeof payload === 'string') {
    return payload;
  }

  if (!payload || typeof payload !== 'object') {
    return null;
  }

  return payload.pay_url || payload.url || null;
}

export function summarizeAlipayHKTradePayPayload(payload = {}) {
  return describeTradePayParams(resolveAlipayHKTradePayParams(payload) || {});
}

export function getAlipayHKBridgeErrorMessage(error) {
  const normalized = normalizeBridgeError(error);

  return (
    normalized.errorDesc ||
    normalized.errorMessage ||
    normalized.message ||
    (normalized.error ? `錯誤碼 ${normalized.error}` : '未知錯誤')
  );
}

export async function callAlipayHKTradePay(payload, timeout = 120000) {
  const params = resolveAlipayHKTradePayParams(payload);

  if (!params) {
    logAlipayHKTradePay('warn', 'missing_trade_pay_params');
    return Promise.reject(new Error('missing_trade_pay_params'));
  }

  logAlipayHKTradePay('info', 'start', describeTradePayParams(params));

  if (hasAlipayHKMiniProgramBridge()) {
    logAlipayHKTradePay('info', 'use_webview_bridge');
    return callBridgeAlipayHKTradePay(params, timeout);
  }

  if (await waitForAlipayHKTradePay()) {
    logAlipayHKTradePay('info', 'use_direct_jsapi');
    return callDirectAlipayHKTradePay(params, timeout);
  }

  logAlipayHKTradePay('error', 'trade_pay_jsapi_unavailable');
  return Promise.reject(new Error('trade_pay_jsapi_unavailable'));
}

export function callAlipayHKMiniProgramApi(api, options = {}, timeout = 120000) {
  if (
    typeof my === 'undefined' ||
    typeof my.postMessage !== 'function'
  ) {
    return Promise.reject(new Error('miniapp_bridge_unavailable'));
  }

  const serialId = createSerialId(api);

  return new Promise((resolve, reject) => {
    const previousHandler = my.onMessage;
    let handleMessage = null;
    let timeoutId = null;

    const cleanup = () => {
      clearTimeout(timeoutId);
      if (my.onMessage === handleMessage) {
        my.onMessage = previousHandler;
      }
    };

    handleMessage = (message) => {
      if (!message || message.serialId !== serialId) {
        if (typeof previousHandler === 'function') {
          previousHandler(message);
        }
        return;
      }

      cleanup();

      if (message.error) {
        reject(normalizeBridgeError(message.error));
        return;
      }

      const result = message.result || message;

      if (result && result.error) {
        reject(normalizeBridgeError(result));
        return;
      }

      resolve(result);
    };

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error('miniapp_bridge_timeout'));
    }, timeout);

    my.onMessage = handleMessage;

    try {
      my.postMessage({
        api,
        options,
        serialId,
      });
    } catch (e) {
      cleanup();
      reject(normalizeBridgeError(e));
    }
  });
}

export async function getAlipayHKAuthCode(scopes) {
  const result = await callAlipayHKMiniProgramApi('getAuthCode', { scopes });
  const authCode = result.authCode || result.authcode || result.auth_code;

  if (!authCode) {
    throw new Error('missing_auth_code');
  }

  return {
    authCode,
    authSuccessScopes:
      result.authSuccessScopes ||
      result.auth_success_scopes ||
      result.authSuccessScope ||
      [],
    authErrorScopes:
      result.authErrorScopes ||
      result.auth_error_scopes ||
      result.authErrorScope ||
      [],
  };
}
